import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { getMyProduct, fetchUsers, createGroup, saveInvitedGroup, updateInvitedGroup, fetchInvitedGroup, editGroup } from '../../../../utils/services/user.service';
import PaymentSidebar from '../../../../shared/components/PaymentSidebar';

const PRODUCTS_PAGE_SIZE = 50;

// Styled Components
const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const DatesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ProductHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ProductRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const AggregateHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 8px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const AggregateRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 8px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DateInput = styled.input`
  &::-webkit-calendar-picker-indicator {
    filter: invert(1);
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const gradientTitle = {
  background: 'linear-gradient(90deg,#00f7d2,#5ce9ff)',
  WebkitBackgroundClip: 'text',
  color: 'transparent',
};

const card = {
  background: '#111522',
  borderRadius: 14,
  padding: 20,
  border: '1px solid rgba(255,255,255,0.06)',
};

const primaryButton = {
  width: '100%',
  padding: '14px',
  borderRadius: 14,
  border: 'none',
  fontWeight: 600,
  background: 'linear-gradient(90deg,#00f7d2,#5ce9ff)',
  color: '#000',
  cursor: 'pointer',
};

const secondaryButton = {
  padding: '10px 16px',
  borderRadius: 12,
  border: '1px solid #2a2f45',
  background: '#0b0e14',
  color: '#eaeaf0',
  cursor: 'pointer',
};

const disabledButton = {
  background: '#1a1f33',
  color: '#5ce9ff',
  cursor: 'not-allowed',
};

function getRemainingQty(adminProducts, key, currentQty) {
  const found = adminProducts.find((p) => p.id === key);
  if (!found || found.maxQty == null) return undefined;
  const remaining = Number(found.maxQty) - (Number(currentQty) || 0);
  return Math.max(0, Number.isNaN(remaining) ? 0 : remaining);
}

function isMaxReached(adminProducts, key, currentQty) {
  const found = adminProducts.find((p) => p.id === key);
  if (!found) return false;
  return currentQty >= found.maxQty;
}

function getMaxQty(adminProducts, key) {
  const found = adminProducts.find((p) => p.id === key);
  return found ? found.maxQty : undefined;
}

export default function GroupPurchase({ groupDetail: groupDetailProp, onGroupDetailRefetch }) {
  console.log("🚀 ~ GroupPurchase ~ groupDetailProp:", groupDetailProp)
  const { storeId: storeIdParam, groupId } = useParams();
  const [effectiveStoreId, setEffectiveStoreId] = useState(storeIdParam);
  
  // Get current user ID from localStorage
  const getCurrentUserId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      return userData?.data?.user?.user_id || userData?.data?.user?._id || userData?.data?.user_id || null;
    } catch (error) {
      console.error('Error getting user ID from localStorage:', error);
      return null;
    }
  };
  
  const currentUserId = getCurrentUserId();
  const storeId = effectiveStoreId || storeIdParam;
  const history = useHistory();
  const [groupDetail, setGroupDetail] = useState(groupDetailProp);
  const [groupTitle, setGroupTitle] = useState('Group Purchase · Pharma Consortium');
  const [storeProducts, setStoreProducts] = useState([]);
  const [storeProductsLoaded, setStoreProductsLoaded] = useState(false);
  const [productsPage, setProductsPage] = useState(1);
  const [totalProductsCount, setTotalProductsCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const addProductScrollRef = useRef(null);
  const addProductSentinelRef = useRef(null);
  // When accessed from store detail (storeIdParam exists but groupId doesn't), force ADMIN role (creation mode)
  // When accessed from groups route (groupId exists), force PHARMACY role (purchase-only mode)
  // In purchase-only mode, users cannot switch to admin view
  const isPurchaseOnlyMode = Boolean(groupId && !storeIdParam);
  const [role, setRole] = useState(storeIdParam && !groupId ? 'ADMIN' : 'PHARMACY');
  const [productQuantities, setProductQuantities] = useState({});
  const [selectedProductsToBuy, setSelectedProductsToBuy] = useState({});
  const [allPharmacies, setAllPharmacies] = useState([]);
  const [pharmaciesLoaded, setPharmaciesLoaded] = useState(false);
  const [savedInviteGroups, setSavedInviteGroups] = useState([]);
  const [selectedPharmacies, setSelectedPharmacies] = useState([]);
  const [inviteGroupName, setInviteGroupName] = useState('');
  const [isSavingInvitedGroup, setIsSavingInvitedGroup] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [participants, setParticipants] = useState([
    { name: 'Farmacia Centrale Milano', qty: 1800, status: 'CONFIRMED', tier: 'CORE' },
    { name: 'Farmacia San Luca Roma', qty: 2400, status: 'PAID', tier: 'PREMIUM' },
    { name: 'Farmacia Europea Torino', qty: 2000, status: 'PENDING', tier: 'STANDARD' },
  ]);
  const [showPharmacySidebar, setShowPharmacySidebar] = useState(false);
  const [showPaymentSidebar, setShowPaymentSidebar] = useState(false);
  const [showInviteSidebar, setShowInviteSidebar] = useState(false);
  const [showEditGroupSidebar, setShowEditGroupSidebar] = useState(false);
  const [editGroupData, setEditGroupData] = useState({
    group_name: '',
    invited_users: [],
    product_details: [],
    start_time: '',
    end_time: '',
    status: 'active',
  });
  const [isUpdatingGroup, setIsUpdatingGroup] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [selectedProductsInEditModal, setSelectedProductsInEditModal] = useState([]);
  const [pharmacySearch, setPharmacySearch] = useState('');
  const [pharmacyCityFilter, setPharmacyCityFilter] = useState('ALL');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedProductsInModal, setSelectedProductsInModal] = useState([]);
  const [adminProducts, setAdminProducts] = useState([]);
  const [startDate, setStartDate] = useState('2026-02-01T09:00');
  const [endDate, setEndDate] = useState('2026-02-10T18:00');
  // Calculate target from sum of product_details.quantity
  const calculateTarget = () => {
    if (groupDetail?.product_details && Array.isArray(groupDetail.product_details)) {
      return groupDetail.product_details.reduce((sum, pd) => sum + (Number(pd.quantity) || 0), 0);
    }
    // Fallback to 10000 if no product_details available
    return 10000;
  };
  const target = calculateTarget();
  const [executed, setExecuted] = useState(false);
  const [auditLog, setAuditLog] = useState([
    { ts: '2026-02-01 9:12', event: 'Group created' },
    { ts: '2026-02-01 10:30', event: 'Farmacia Centrale Milano joined' },
    { ts: '2026-02-01 11:05', event: 'Farmacia San Luca Roma escrow PAID' },
  ]);
  const [isSavingGroup, setIsSavingGroup] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const isViewMode = Boolean(groupId);

  // Update current time every minute to refresh the time bar
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, []);

  // Enforce PHARMACY role in purchase-only mode (when accessed from groups route)
  useEffect(() => {
    if (isPurchaseOnlyMode && role !== 'PHARMACY') {
      setRole('PHARMACY');
    }
  }, [isPurchaseOnlyMode, role]);

  const mapProduct = (p) => ({
    id: p._id,
    name: p.title || 'Untitled',
    price: p.product_sizes_quantity?.[0]?.price ?? p.euro_price?.[0] ?? 0,
    description: p.description || '',
    category_id: p.category_id || null,
    vat: p.vat || 0, // VAT percentage
    image: p.pictures?.[0] || p.image || p.product_image || p.coverImage || null,
  });

  // Set groupDetail from prop when it changes
  useEffect(() => {
    if (groupDetailProp) {
      console.log("🚀 ~ GroupPurchase ~ groupDetailProp received:", groupDetailProp);
      setGroupDetail(groupDetailProp);
    }
  }, [groupDetailProp]);

  useEffect(() => {
    if (!groupDetail) return;
    const g = groupDetail;
    setGroupTitle(g.group_name || g.name || 'Group Purchase');
    if (g.product_details && Array.isArray(g.product_details)) {
      // Create a map of product_id to product title from product_detail array
      const productDetailMap = {};
      if (g.product_detail && Array.isArray(g.product_detail)) {
        g.product_detail.forEach((product) => {
          if (product._id && product.title) {
            productDetailMap[product._id] = product.title;
          }
        });
      }
      
      const mapped = g.product_details.map((pd) => {
        const productId = pd.product_id || pd._id || pd.id;
        // Get product name from product_detail map first, then fallback to other sources
        const productName = productDetailMap[productId] || pd.product_name || pd.title || pd.name || 'Product';
        // Get category_id and vat from product_detail array
        const productDetail = g.product_detail?.find((p) => (p._id || p.id) === productId);
        return {
          id: productId,
          name: productName,
          price: Number(pd.price) || 0,
          maxQty: Number(pd.quantity ?? pd.maxQty ?? pd.max_quantity ?? 0) || 0,
          category_id: productDetail?.category_id || null,
          vat: productDetail?.vat || 0, // VAT percentage
        };
      });
      setAdminProducts(mapped);
    }
    if (g.start_time) setStartDate(new Date(g.start_time).toISOString().slice(0, 16));
    if (g.end_time) setEndDate(new Date(g.end_time).toISOString().slice(0, 16));
    if (Array.isArray(g.participants)) setParticipants(g.participants);
    if (Array.isArray(g.user_id)) setSelectedPharmacies(g.user_id);
    
    // Build audit log: start with "Group created" using createdAt, then add purchase entries from order_data
    const auditEntries = [];
    
    // Add "Group created" entry
    if (g.createdAt) {
      const createdAtDate = new Date(g.createdAt);
      const year = createdAtDate.getFullYear();
      const month = String(createdAtDate.getMonth() + 1).padStart(2, '0');
      const day = String(createdAtDate.getDate()).padStart(2, '0');
      const hours = String(createdAtDate.getHours()).padStart(2, '0');
      const minutes = String(createdAtDate.getMinutes()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}`;
      auditEntries.push({ ts: formattedDate, event: 'Group created' });
    }
    
    // Add purchase entries from order_data.order_details
    if (Array.isArray(g.order_data) && g.order_data.length > 0) {
      g.order_data.forEach((orderData) => {
        if (Array.isArray(orderData.order_details) && orderData.order_details.length > 0) {
          orderData.order_details.forEach((orderDetail) => {
            if (orderDetail.createdAt && orderDetail.user_info?.name) {
              const purchaseDate = new Date(orderDetail.createdAt);
              const year = purchaseDate.getFullYear();
              const month = String(purchaseDate.getMonth() + 1).padStart(2, '0');
              const day = String(purchaseDate.getDate()).padStart(2, '0');
              const hours = String(purchaseDate.getHours()).padStart(2, '0');
              const minutes = String(purchaseDate.getMinutes()).padStart(2, '0');
              const formattedDate = `${year}-${month}-${day} ${hours}:${minutes}`;
              const buyerName = orderDetail.user_info.name;
              auditEntries.push({ 
                ts: formattedDate, 
                event: `${buyerName} escrow PAID` 
              });
            }
          });
        }
      });
    }
    
    // Add any existing audit_log entries (if they exist)
    if (Array.isArray(g.audit_log) && g.audit_log.length > 0) {
      const existingLogs = g.audit_log.map((a) => ({ ts: a.ts || a.timestamp, event: a.event || a.message }));
      auditEntries.push(...existingLogs);
    }
    
    // Sort entries by timestamp (oldest first)
    auditEntries.sort((a, b) => {
      const dateA = new Date(a.ts.replace(/(\d{4}-\d{2}-\d{2}) (\d{2}):(\d{2})/, '$1T$2:$3:00'));
      const dateB = new Date(b.ts.replace(/(\d{4}-\d{2}-\d{2}) (\d{2}):(\d{2})/, '$1T$2:$3:00'));
      return dateA - dateB;
    });
    
    if (auditEntries.length > 0) {
      setAuditLog(auditEntries);
    }
    
    if (g.executed) setExecuted(true);
    if (g.store_id) setEffectiveStoreId(g.store_id);
  }, [groupDetail]);
  
  // Check if current user is the group owner
  const isGroupOwner = useMemo(() => {
    if (!currentUserId) return false;
    // In creation mode (no groupId, accessed from store detail), treat user as owner
    if (!isViewMode && storeIdParam) return true;
    // In view mode, owner is the user that created the group
    return groupDetail?.user_id === currentUserId || groupDetailProp?.user_id === currentUserId;
  }, [currentUserId, groupDetail?.user_id, groupDetailProp?.user_id, isViewMode, storeIdParam]);
  
  // Check if current user is invited to the group
  const isInvitedUser = useMemo(() => {
    if (!currentUserId) return false;
    const invitedUsers = groupDetail?.invited_users || groupDetailProp?.invited_users || [];
    const invitedUsersObjIds = groupDetail?.invitedUsersObjIds || groupDetailProp?.invitedUsersObjIds || [];
    // Check if current user ID is in invited_users array or invitedUsersObjIds array
    return invitedUsers.includes(currentUserId) || 
           invitedUsersObjIds.includes(currentUserId) ||
           invitedUsers.some(id => String(id) === String(currentUserId)) ||
           invitedUsersObjIds.some(id => String(id) === String(currentUserId));
  }, [currentUserId, groupDetail?.invited_users, groupDetail?.invitedUsersObjIds, groupDetailProp?.invited_users, groupDetailProp?.invitedUsersObjIds]);
  
  // Check if group has ended
  const isGroupEnded = useMemo(() => {
    if (!groupDetail?.end_time) return false;
    const endTime = new Date(groupDetail.end_time).getTime();
    const now = Date.now();
    return endTime <= now;
  }, [groupDetail?.end_time]);
  
  // User can only purchase if they are invited (or if there are no restrictions) AND group hasn't ended
  const canPurchase = !isGroupEnded && (isInvitedUser || !groupDetail?.invited_users || (Array.isArray(groupDetail?.invited_users) && groupDetail.invited_users.length === 0));

  // Helper function to get adhering pharmacies with user names and locations
  const getAdheringPharmacies = () => {
    if (!groupDetail) {
      console.log("🚀 ~ getAdheringPharmacies ~ no groupDetail");
      return [];
    }
    const g = groupDetail;
    console.log("🚀 ~ getAdheringPharmacies ~ groupDetail:", g);
    
    // Check for user data - API returns user_detail array
    // Also check for user array as fallback
    const users = Array.isArray(g.user_detail) ? g.user_detail :
                  Array.isArray(g.user) ? g.user : 
                  Array.isArray(g.users) ? g.users : 
                  [];
    
    const locations = Array.isArray(g.locations) ? g.locations : [];
    
    console.log("🚀 ~ getAdheringPharmacies ~ users array:", users);
    console.log("🚀 ~ getAdheringPharmacies ~ users length:", users.length);
    console.log("🚀 ~ getAdheringPharmacies ~ locations array:", locations);
    console.log("🚀 ~ getAdheringPharmacies ~ locations length:", locations.length);
    
    if (users.length === 0) {
      console.log("🚀 ~ getAdheringPharmacies ~ no users found. Available keys:", Object.keys(g || {}));
      return [];
    }
    
    // Match users with their locations
    const result = users.map((user) => {
      if (!user || typeof user !== 'object') {
        console.log("🚀 ~ Invalid user object:", user);
        return null;
      }
      
      const userId = user._id || user.id;
      console.log("🚀 ~ Processing user:", user.name, "with ID:", userId);
      
      const location = locations.find((loc) => {
        const locUserId = loc.user_id || loc.userId;
        return locUserId === userId;
      });
      
      const locationStr = location 
        ? `${location.city || ''}${location.city && location.country ? ', ' : ''}${location.country || ''}`.trim() 
        : 'Location not available';
      
      return {
        id: userId,
        name: user.name || user.email || 'Unknown',
        email: user.email || '',
        city: location?.city || '',
        country: location?.country || '',
        location: locationStr,
      };
    }).filter(Boolean); // Remove any null entries
    
    console.log("🚀 ~ getAdheringPharmacies ~ final result:", result);
    return result;
  };


  useEffect(() => {
    if (!storeId) return;
    const fetchStoreProducts = async () => {
      setStoreProductsLoaded(false);
      setProductsPage(1);
      try {
        const response = await getMyProduct({
          store_id: storeId,
          limit: PRODUCTS_PAGE_SIZE,
          pageNumber: 1,
        });
        const count = response.data?.count ?? 0;
        setTotalProductsCount(count);
        if (response.data?.products && Array.isArray(response.data.products)) {
          const mapped = response.data.products.map(mapProduct);
          setStoreProducts(mapped);
          setProductsPage(2);
        } else {
          setStoreProducts([]);
        }
      } catch (err) {
        console.error('Error fetching store products:', err);
        setStoreProducts([]);
      } finally {
        setStoreProductsLoaded(true);
      }
    };
    fetchStoreProducts();
  }, [storeId]);

  const loadMoreProducts = useCallback(async () => {
    if (!storeId || isLoadingMore) return;
    if (storeProducts.length >= totalProductsCount) return;
    setIsLoadingMore(true);
    try {
      const response = await getMyProduct({
        store_id: storeId,
        limit: PRODUCTS_PAGE_SIZE,
        pageNumber: productsPage,
      });
      if (response.data?.products && Array.isArray(response.data.products)) {
        const mapped = response.data.products.map(mapProduct);
        setStoreProducts((prev) => [...prev, ...mapped]);
        setProductsPage((p) => p + 1);
      }
    } catch (err) {
      console.error('Error loading more products:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [storeId, productsPage, isLoadingMore, storeProducts.length, totalProductsCount]);

  useEffect(() => {
    if ((!showAddProductModal && !showEditProductModal) || !addProductScrollRef.current || !addProductSentinelRef.current) return;
    const hasMore = storeProducts.length < totalProductsCount;
    if (!hasMore || isLoadingMore || !storeProductsLoaded) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreProducts();
      },
      { root: addProductScrollRef.current, rootMargin: '80px', threshold: 0 },
    );
    observer.observe(addProductSentinelRef.current);
    return () => observer.disconnect();
  }, [showAddProductModal, showEditProductModal, storeProducts.length, totalProductsCount, isLoadingMore, storeProductsLoaded, loadMoreProducts]);

  useEffect(() => {
    if (!showInviteSidebar && !showEditGroupSidebar) return;
    const loadPharmacies = async () => {
      setPharmaciesLoaded(false);
      try {
        const payload = {
          role :["pharmacy","rsa", "hospital" ]
        }
        const response = await fetchUsers(payload);
        const data = response.data;
        const raw = Array.isArray(data?.users) ? data.users : Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        const mapped = raw.map((u) => ({
          id: u._id || u.user_id || u.id,
          name: u.name || u.email || 'Unknown',
          city: u.location?.[0]?.city || u.city || '',
        }));
        setAllPharmacies(mapped);
      } catch (err) {
        console.error('Error fetching pharmacies:', err);
        setAllPharmacies([]);
      } finally {
        setPharmaciesLoaded(true);
      }
    };
    loadPharmacies();
  }, [showInviteSidebar, showEditGroupSidebar]);

  // Load store products when edit sidebar or edit product modal opens (if not already loaded)
  useEffect(() => {
    if ((!showEditGroupSidebar && !showEditProductModal) || storeProductsLoaded || !storeId) return;
    const loadProducts = async () => {
      try {
        const response = await getMyProduct({ store_id: storeId, page: 1, limit: 100 });
        const data = response.data;
        const products = data?.products || data?.data || [];
        setStoreProducts(products);
        setStoreProductsLoaded(true);
      } catch (err) {
        console.error('Error fetching store products for edit:', err);
      }
    };
    loadProducts();
  }, [showEditGroupSidebar, showEditProductModal, storeProductsLoaded, storeId]);

  // Fetch saved invited groups on component mount (response: { data: [{ _id, group_name, invited_users, user_detail }] })
  const loadSavedGroups = useCallback(async () => {
    try {
      const response = await fetchInvitedGroup();
      if (response.data?.data && Array.isArray(response.data.data)) {
        const mapped = response.data.data.map((group) => ({
          id: group._id,
          name: group.group_name || group.name || 'Untitled Group',
          pharmacyIds: group.invited_users || group.invited_user || [],
          user_detail: group.user_detail || [],
        }));
        setSavedInviteGroups(mapped);
      } else if (response.data?.groups && Array.isArray(response.data.groups)) {
        const mapped = response.data.groups.map((group) => ({
          id: group._id,
          name: group.group_name || group.name || 'Untitled Group',
          pharmacyIds: group.invited_users || group.invited_user || [],
          user_detail: group.user_detail || [],
        }));
        setSavedInviteGroups(mapped);
      }
    } catch (err) {
      console.error('Error fetching invited groups:', err);
    }
  }, []);

  useEffect(() => {
    loadSavedGroups();
  }, [loadSavedGroups]);

  const pharmacyProducts = adminProducts.map((p) => ({
    key: p.id,
    label: p.name,
    price: `€${p.price.toFixed(2)}`,
  }));
  console.log("🚀 ~ GroupPurchase ~ pharmacyProducts:", pharmacyProducts)

  // Use end_time from groupDetail if available, otherwise use endDate state
  const effectiveEndTime = groupDetail?.end_time || endDate;
  const effectiveStartTime = groupDetail?.start_time || startDate;
  
  const startTs = new Date(effectiveStartTime).getTime();
  const endTs = new Date(effectiveEndTime).getTime();
  const nowTs = currentTime; // Use state to trigger re-renders
  const totalDuration = Math.max(1, endTs - startTs);
  const elapsed = Math.min(totalDuration, Math.max(0, nowTs - startTs));
  const timeProgress = Math.round((elapsed / totalDuration) * 100);
  const remainingMs = Math.max(0, endTs - nowTs);
  const remainingHours = Math.floor(remainingMs / 36e5);
  const remainingMinutes = Math.floor((remainingMs % 36e5) / 6e4);
  // Calculate total volume from API aggregated_volume if available
  const getTotalVolumeFromAPI = () => {
    if (!groupDetail?.order_data || !Array.isArray(groupDetail.order_data) || groupDetail.order_data.length === 0) {
      return null;
    }
    const orderData = groupDetail.order_data[0];
    const aggregatedVolume = orderData?.aggregated_volume || [];
    if (aggregatedVolume.length === 0) {
      return null;
    }
    return aggregatedVolume.reduce((sum, item) => sum + (item.total_quantity || 0), 0);
  };
  
  const apiTotalVolume = getTotalVolumeFromAPI();
  const baseVolume = participants.reduce((s, p) => s + p.qty, 0);
  const selectedQty = Object.entries(productQuantities).reduce(
    (s, [id, q]) => s + (selectedProductsToBuy[id] ? (q || 0) : 0),
    0
  );
  const subtotalAmount = adminProducts.reduce((sum, prod) => {
    if (!selectedProductsToBuy[prod.id]) return sum;
    const qty = productQuantities[prod.id] || 0;
    return sum + qty * (prod.price || 0);
  }, 0);
  const shippingCost = 25; // Shipping cost
  const totalAmountToPay = subtotalAmount + shippingCost; // Total includes shipping + price
  const hasSelectionWithQty = Object.keys(selectedProductsToBuy).some((id) => selectedProductsToBuy[id] && (productQuantities[id] || 0) > 0);
  // Use API total volume if available, otherwise use calculated volume
  const current = apiTotalVolume !== null ? apiTotalVolume : (baseVolume + selectedQty);
  const progress = Math.min(100, Math.round((current / target) * 100));
  // Get aggregated volume from API response if available
  const getAggregatedVolumeFromAPI = () => {
    if (!groupDetail?.order_data || !Array.isArray(groupDetail.order_data) || groupDetail.order_data.length === 0) {
      return null;
    }
    
    const orderData = groupDetail.order_data[0];
    const aggregatedVolume = orderData?.aggregated_volume || [];
    const orderDetails = orderData?.order_details || [];
    
    if (aggregatedVolume.length === 0) {
      return null;
    }
    
    // Create a map of product_id to product name from order_details
    const productNameMap = {};
    orderDetails.forEach((order) => {
      if (order.product_details?.product_id && order.product_details?.title) {
        productNameMap[order.product_details.product_id] = order.product_details.title;
      }
    });
    
    // Also check product_detail array for names
    if (groupDetail.product_detail && Array.isArray(groupDetail.product_detail)) {
      groupDetail.product_detail.forEach((product) => {
        if (product._id && product.title) {
          productNameMap[product._id] = product.title;
        }
      });
    }
    
    // Build aggregated data
    return aggregatedVolume.map((item) => {
      const productId = item._id;
      const qty = item.total_quantity || 0;
      
      // Find product price from product_details or product_detail
      let price = 0;
      if (groupDetail.product_details && Array.isArray(groupDetail.product_details)) {
        const productDetail = groupDetail.product_details.find((pd) => pd.product_id === productId);
        if (productDetail) {
          price = productDetail.price || 0;
        }
      }
      
      // Count unique pharmacies/buyers from order_details for this product
      const productOrders = orderDetails.filter((order) => order.product_details?.product_id === productId);
      const uniqueBuyers = new Set(productOrders.map((order) => order.buyer_user_id).filter(Boolean));
      const pharmaciesCount = uniqueBuyers.size;
      
      // Calculate weighted score (simplified - you can enhance this based on user tiers if available)
      const weightedScore = pharmaciesCount;
      
      return {
        id: productId,
        name: productNameMap[productId] || 'Unknown Product',
        qty,
        value: qty * price,
        pharmaciesCount,
        weightedScore,
      };
    });
  };
  
  const tierWeight = { CORE: 1, STANDARD: 1, PREMIUM: 1.5 };
  
  // Use API data if available, otherwise fall back to calculated data
  const apiAggregatedData = getAggregatedVolumeFromAPI();
  const aggregatedByProduct = apiAggregatedData || adminProducts.map((prod) => {
    const qty = selectedProductsToBuy[prod.id] ? (productQuantities[prod.id] || 0) : 0;
    const buyers = participants.filter((p) => (p.items?.[prod.id] ?? qty) > 0);
    const pharmaciesCount = buyers.length;
    const weightedScore = buyers.reduce((s, p) => s + (tierWeight[p.tier] || 1), 0);
    return {
      id: prod.id,
      name: prod.name,
      qty,
      value: qty * prod.price,
      pharmaciesCount,
      weightedScore,
    };
  });
  const paidCount = participants.filter((p) => p.status === 'PAID').length;
  const paidRatio = participants.length > 0 ? paidCount / participants.length : 0;
  const isExecutable = current >= target && paidRatio >= 0.7;

  const handleSavePurchaseProposal = async () => {
    const product_details = adminProducts.map((p) => ({
      product_id: p.id,
      price: Number(p.price),
      quantity: Number(p.maxQty),
    }));
    const payload = {
      group_name: groupTitle,
      product_details,
      start_time: new Date(startDate).toISOString(),
      end_time: new Date(endDate).toISOString(),
      user_id: selectedPharmacies,
      store_id: storeId,
    };
    setIsSavingGroup(true);
    try {
      await createGroup(payload);
      toast.success('Group created successfully');
      setAuditLog([...auditLog, { ts: new Date().toISOString(), event: 'Purchase proposal saved and group created' }]);
      // Redirect to homeproducts after successful group creation
      setTimeout(() => {
        history.push('/homeproducts');
      }, 100);
    } catch (err) {
      console.error('Error creating group:', err);
      toast.error(err.response?.data?.message ||err.response?.data?.Message || 'Failed to create group');
    } finally {
      setIsSavingGroup(false);
    }
  };

  // Open Edit Group sidebar with current group data
  const handleOpenEditGroup = () => {
    if (!groupDetail) return;
    
    // Get current invited users
    const currentInvitedUsers = groupDetail.invited_users || groupDetail.invitedUsersObjIds || [];
    
    // Get current product details
    const currentProductDetails = (groupDetail.product_details || []).map((pd) => ({
      product_id: pd.product_id,
      price: pd.price || 0,
      quantity: pd.quantity || 0,
      // Get product name from product_detail array if available
      name: groupDetail.product_detail?.find((p) => p._id === pd.product_id)?.title || pd.product_id,
    }));
    
    setEditGroupData({
      group_name: groupDetail.group_name || groupTitle,
      invited_users: currentInvitedUsers,
      product_details: currentProductDetails,
      start_time: groupDetail.start_time ? new Date(groupDetail.start_time).toISOString().slice(0, 16) : startDate,
      end_time: groupDetail.end_time ? new Date(groupDetail.end_time).toISOString().slice(0, 16) : endDate,
      status: groupDetail.status || 'active',
    });
    setShowEditGroupSidebar(true);
  };

  // Handle updating the group
  const handleUpdateGroup = async () => {
    if (!groupDetail?._id) {
      toast.error('Group ID not found');
      return;
    }
    
    setIsUpdatingGroup(true);
    try {
      const payload = {
        group_id: groupDetail._id,
        group_name: editGroupData.group_name,
        product_details: editGroupData.product_details.map((pd) => ({
          product_id: pd.product_id,
          price: Number(pd.price),
          quantity: Number(pd.quantity),
        })),
        start_time: new Date(editGroupData.start_time).toISOString(),
        end_time: new Date(editGroupData.end_time).toISOString(),
        status: editGroupData.status || 'active',
      };
      
      await editGroup(payload);
      toast.success('Group updated successfully');
      setShowEditGroupSidebar(false);
      
      // Refetch group details to get updated data
      if (onGroupDetailRefetch) {
        onGroupDetailRefetch();
      }
    } catch (err) {
      console.error('Error updating group:', err);
      toast.error(err.response?.data?.message || 'Failed to update group');
    } finally {
      setIsUpdatingGroup(false);
    }
  };

  // Remove pharmacy from edit group
  const handleRemovePharmacyFromEdit = (pharmacyId) => {
    setEditGroupData((prev) => ({
      ...prev,
      invited_users: prev.invited_users.filter((id) => id !== pharmacyId),
    }));
  };

  // Add product to edit group
  const handleAddProductToEdit = (product) => {
    const exists = editGroupData.product_details.find((pd) => pd.product_id === product.id);
    if (!exists) {
      setEditGroupData((prev) => ({
        ...prev,
        product_details: [
          ...prev.product_details,
          {
            product_id: product.id,
            price: product.price || 0,
            quantity: product.maxQty || 100,
            name: product.name || product.title || product.id,
          },
        ],
      }));
    }
  };

  // Remove product from edit group
  const handleRemoveProductFromEdit = (productId) => {
    setEditGroupData((prev) => ({
      ...prev,
      product_details: prev.product_details.filter((pd) => pd.product_id !== productId),
    }));
  };

  // Update product quantity in edit group
  const handleUpdateProductQuantityInEdit = (productId, quantity) => {
    setEditGroupData((prev) => ({
      ...prev,
      product_details: prev.product_details.map((pd) =>
        pd.product_id === productId ? { ...pd, quantity: Number(quantity) } : pd
      ),
    }));
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0b0e14', color: '#eaeaf0', padding: 32 }}>
      <header style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <button
            onClick={() => (isViewMode ? history.push('/homeproducts') : history.push(`/storedetail/${storeId}`))}
            style={{ ...secondaryButton, marginBottom: 12 }}
          >
            {isViewMode ? '← Back to Groups' : '← Back to Store'}
          </button>
          <h1 style={{ fontSize: 34, margin: 0, ...gradientTitle }}>{groupTitle}</h1>
          <p style={{ opacity: 0.7 }}>Action governed purchase – prices locked by consortium admin</p>
        </div>
        {/* Hide switch button in purchase-only mode (from groups route) and creation mode (from store detail) */}
        {/* Switch is only available in other contexts if needed */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {!isPurchaseOnlyMode && !storeIdParam && (
            <button onClick={() => setRole(role === 'ADMIN' ? 'PHARMACY' : 'ADMIN')} style={secondaryButton}>
              Switch to {role === 'ADMIN' ? 'Pharmacy' : 'Admin'} view
            </button>
          )}
          {/* Edit Group button - only visible to group owner in view mode */}
          {isGroupOwner && isViewMode && (
            <button 
              onClick={handleOpenEditGroup} 
              style={{ 
                ...secondaryButton, 
                background: 'rgba(0, 247, 210, 0.1)', 
                borderColor: 'rgba(0, 247, 210, 0.4)',
                color: '#00f7d2',
              }}
            >
              ✏️ Edit Group
            </button>
          )}
        </div>
      </header>

      <MainLayout>
        {/* Only show ADMIN view if user is the group owner and not in purchase-only mode */}
        {role === 'ADMIN' && !isPurchaseOnlyMode && isGroupOwner && (
          <section style={card}>
            <h2>Purchase Proposal · Admin Definition</h2>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, opacity: 0.7, display: 'block', textAlign: 'left' }}>Group Purchase name</label>
              <input
                value={groupTitle}
                onChange={(e) => setGroupTitle(e.target.value)}
                style={{
                  width: '100%', marginTop: 6, background: '#0b0e14', border: '1px solid #2a2f45',
                  color: '#fff', borderRadius: 10, padding: 10,
                }}
              />
            </div>
            <p style={{ fontSize: 13, opacity: 0.7 }}>Manage products included in this Action.</p>
            {savedInviteGroups.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, opacity: 0.7, display: 'block', textAlign: 'left', marginBottom: 8 }}>Saved groups</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {savedInviteGroups.map((g) => {
                    const isActive = (g.pharmacyIds?.length || 0) > 0 && selectedPharmacies.length === (g.pharmacyIds?.length || 0) && (g.pharmacyIds || []).every((id) => selectedPharmacies.includes(id));
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => setSelectedPharmacies(g.pharmacyIds || [])}
                        style={{
                          ...secondaryButton,
                          padding: '8px 14px',
                          borderRadius: 10,
                          fontSize: 13,
                          color: isActive ? '#0b0e14' : '#eaeaf0',
                          borderColor: isActive ? 'rgba(0,247,210,0.6)' : undefined,
                          background: isActive ? 'rgba(0,247,210,0.25)' : '#0b0e14',
                        }}
                      >
                        {g.name} {g.pharmacyIds?.length ? `(${g.pharmacyIds.length})` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <button style={{ ...secondaryButton, marginBottom: 12 }} onClick={() => setShowInviteSidebar(true)}>
              Invite pharmacies
            </button>
            {storeId && (
              <button style={{ ...secondaryButton, marginBottom: 16 }} onClick={() => {
                setShowAddProductModal(true);
                setProductSearchTerm('');
                setSelectedProductsInModal([]);
              }}>
                Add product from store
              </button>
            )}
            <div style={{ marginBottom: 16 }}>
              {selectedPharmacies.length === 0 ? (
                <p style={{ fontSize: 13, opacity: 0.6, color: '#ffb84d', margin: 0 }}>Please invite pharmacies first</p>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {selectedPharmacies.map((pharmacyId) => {
                    const pharmacy = allPharmacies.find((p) => p.id === pharmacyId);
                    const label = pharmacy ? `${pharmacy.name}${pharmacy.city ? ` (${pharmacy.city})` : ''}` : pharmacyId;
                    return (
                      <span
                        key={pharmacyId}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 10px',
                          background: 'rgba(0,247,210,0.12)',
                          border: '1px solid rgba(0,247,210,0.3)',
                          borderRadius: 8,
                          fontSize: 13,
                          color: '#eaeaf0',
                        }}
                      >
                        {label}
                        <button
                          type="button"
                          onClick={() => setSelectedPharmacies((prev) => prev.filter((id) => id !== pharmacyId))}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#eaeaf0',
                            cursor: 'pointer',
                            padding: 0,
                            marginLeft: 2,
                            fontSize: 14,
                            lineHeight: 1,
                            opacity: 0.8,
                          }}
                          aria-label="Remove"
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
            <DatesContainer style={{ marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, opacity: 0.7, display: 'block', textAlign: 'left' }}>Start date</label>
                <DateInput
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ 
                    width: '100%', 
                    marginTop: 4, 
                    background: '#0b0e14', 
                    border: '1px solid #2a2f45', 
                    color: '#fff', 
                    borderRadius: 8, 
                    padding: 8,
                  }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, opacity: 0.7, display: 'block', textAlign: 'left' }}>End date</label>
                <DateInput
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ 
                    width: '100%', 
                    marginTop: 4, 
                    background: '#0b0e14', 
                    border: '1px solid #2a2f45', 
                    color: '#fff', 
                    borderRadius: 8, 
                    padding: 8,
                  }}
                />
              </div>
            </DatesContainer>
            <ProductHeader style={{ fontSize: 13, opacity: 0.7, marginBottom: 8, alignItems: 'end' }}>
              <strong style={{ textAlign: 'left' }}>Product</strong>
              <strong style={{ textAlign: 'left' }}>Unit price</strong>
              <strong style={{ textAlign: 'left' }}>Max qty</strong>
              <span />
            </ProductHeader>
            {adminProducts.map((p, idx) => (
              <ProductRow
                key={p.id}
                style={{
                  alignItems: 'center',
                  padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <input
                  value={p.name}
                  onChange={(e) => {
                    const next = [...adminProducts];
                    next[idx] = { ...p, name: e.target.value };
                    setAdminProducts(next);
                  }}
                  style={{ background: '#0b0e14', border: '1px solid #2a2f45', color: '#fff', borderRadius: 8, padding: 8, textAlign: 'left' }}
                />
                <input
                  type="number"
                  step={0.01}
                  value={p.price}
                  onChange={(e) => {
                    const next = [...adminProducts];
                    next[idx] = { ...p, price: Number(e.target.value) };
                    setAdminProducts(next);
                  }}
                  style={{ background: '#0b0e14', border: '1px solid #2a2f45', color: '#fff', borderRadius: 8, padding: 8, textAlign: 'left' }}
                />
                <input
                  type="number"
                  value={p.maxQty}
                  onChange={(e) => {
                    const next = [...adminProducts];
                    next[idx] = { ...p, maxQty: Number(e.target.value) };
                    setAdminProducts(next);
                  }}
                  style={{ background: '#0b0e14', border: '1px solid #2a2f45', color: '#fff', borderRadius: 8, padding: 8, textAlign: 'left' }}
                />
                <button
                  onClick={() => {
                    const next = adminProducts.filter((_, i) => i !== idx);
                    setAdminProducts(next);
                    setAuditLog([...auditLog, { ts: new Date().toISOString(), event: `Product ${p.name} removed from action` }]);
                  }}
                  style={{ ...secondaryButton, padding: '6px 10px' }}
                >
                  Remove
                </button>
              </ProductRow>
            ))}
            {(() => {
              const isFormValid =
                groupTitle.trim() !== '' &&
                adminProducts.length > 0 &&
                startDate &&
                endDate &&
                selectedPharmacies.length > 0;
              return (
            <button
              style={{ ...primaryButton, marginTop: 16, ...(isSavingGroup || !isFormValid ? disabledButton : {}) }}
              disabled={isSavingGroup || !isFormValid}
              onClick={() => {
                if (isSavingGroup || !isFormValid) return;
                handleSavePurchaseProposal();
              }}
            >
              {isSavingGroup ? 'Saving...' : 'Save Purchase Proposal'}
            </button>
              );
            })()}
          </section>
        )}

        {role === 'PHARMACY' && (
          <section style={card}>
            <h2>Purchase Proposal · Pharmacy View</h2>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.7, marginBottom: 12 }}>
              <span>Start: {new Date(startDate).toLocaleString()}</span>
              <span>End: {new Date(endDate).toLocaleString()}</span>
            </div>
            <div style={{ marginBottom: 16, overflowX: 'auto', minWidth: 0 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 2fr 1fr 1fr 1fr', gap: 12, fontSize: 13, opacity: 0.7, minWidth: 320 }}>
                <strong>Select</strong>
                <strong>Product</strong>
                <strong>Unit price</strong>
                <strong>Quantity</strong>
                <strong>Remaining</strong>
              </div>
              {pharmacyProducts.map((p) => {
                const isSelected = selectedProductsToBuy[p.key];
                const maxQty = getMaxQty(adminProducts, p.key) || 0;
                const availableStock = maxQty; // Available stock before any selection
                const hasStockAvailable = availableStock > 0;
                
                return (
                  <div
                    key={p.key}
                    style={{
                      display: 'grid', gridTemplateColumns: 'auto 2fr 1fr 1fr 1fr', gap: 12, alignItems: 'center',
                      padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)', minWidth: 320,
                      opacity: !hasStockAvailable ? 0.5 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!isSelected}
                      disabled={!hasStockAvailable}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        if (!hasStockAvailable && checked) return; // Prevent selection if no stock
                        setSelectedProductsToBuy((prev) => ({ ...prev, [p.key]: checked }));
                        if (!checked) {
                          setProductQuantities((prev) => {
                            const next = { ...prev };
                            delete next[p.key];
                            return next;
                          });
                        } else if (hasStockAvailable && !productQuantities[p.key]) {
                          // Auto-set quantity to 1 if available stock exists
                          setProductQuantities((prev) => ({
                            ...prev,
                            [p.key]: Math.min(1, availableStock),
                          }));
                        }
                      }}
                      style={{ 
                        width: 18, 
                        height: 18, 
                        cursor: hasStockAvailable ? 'pointer' : 'not-allowed',
                        opacity: hasStockAvailable ? 1 : 0.5,
                      }}
                      title={!hasStockAvailable ? 'No stock available' : ''}
                    />
                    <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setSelectedProduct(p.key)}>
                      {p.label}
                    </span>
                    <span>{p.price}</span>
                    <input
                      type="number"
                      min={0}
                      max={availableStock}
                      value={productQuantities[p.key] ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          setProductQuantities((prev) => ({ ...prev, [p.key]: undefined }));
                          return;
                        }
                        const val = Number(raw);
                        if (Number.isNaN(val)) {
                          setProductQuantities((prev) => ({ ...prev, [p.key]: undefined }));
                          return;
                        }
                        // Enforce max: cannot exceed available stock
                        const clampedVal = Math.min(availableStock, Math.max(0, val));
                        setProductQuantities((prev) => ({
                          ...prev,
                          [p.key]: clampedVal,
                        }));
                      }}
                      onBlur={(e) => {
                        // Ensure value doesn't exceed available stock on blur
                        const val = Number(e.target.value);
                        if (!Number.isNaN(val) && val > availableStock) {
                          setProductQuantities((prev) => ({
                            ...prev,
                            [p.key]: availableStock,
                          }));
                        }
                      }}
                      disabled={!isSelected || !hasStockAvailable}
                      style={{
                        padding: 8, 
                        background: '#0b0e14', 
                        border: '1px solid #2a2f45', 
                        color: '#fff', 
                        borderRadius: 8,
                        opacity: (isSelected && hasStockAvailable) ? 1 : 0.5, 
                        cursor: (isSelected && hasStockAvailable) ? 'text' : 'not-allowed',
                      }}
                      title={!hasStockAvailable ? 'No stock available' : `Maximum: ${availableStock}`}
                    />
                    <span
                      style={{
                        fontSize: 12, padding: '4px 8px', borderRadius: 999,
                        background: isMaxReached(adminProducts, p.key, productQuantities[p.key]) ? 'rgba(0,247,210,0.15)' : 'rgba(255,184,77,0.15)',
                        color: isMaxReached(adminProducts, p.key, productQuantities[p.key]) ? '#00f7d2' : '#ffb84d',
                        textAlign: 'center',
                      }}
                    >
                      {isMaxReached(adminProducts, p.key, productQuantities[p.key])
                        ? 'MAX'
                        : getRemainingQty(adminProducts, p.key, productQuantities[p.key])?.toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>
            {isGroupEnded && (
              <div style={{
                marginBottom: 12,
                padding: '12px',
                borderRadius: 8,
                background: 'rgba(107, 114, 128, 0.1)',
                border: '1px solid rgba(107, 114, 128, 0.3)',
                color: '#9ca3af',
                fontSize: 12,
                textAlign: 'center',
              }}>
                ⏱️ This group purchase has ended. You can view the group details but cannot make a purchase.
              </div>
            )}
            <button
              disabled={!hasSelectionWithQty || isGroupOwner || !canPurchase}
              onClick={() => setShowPaymentSidebar(true)}
              style={{
                ...primaryButton,
                ...((!hasSelectionWithQty || isGroupOwner || !canPurchase) ? disabledButton : {}),
                cursor: (hasSelectionWithQty && !isGroupOwner && canPurchase) ? 'pointer' : 'not-allowed',
                opacity: (isGroupOwner || !canPurchase) ? 0.5 : 1,
              }}
              title={
                isGroupOwner 
                  ? 'You are the owner of this group' 
                  : isGroupEnded
                    ? 'This group purchase has ended'
                    : !canPurchase 
                      ? 'You are not invited to this group. View only.' 
                      : ''
              }
            >
              {isGroupOwner 
                ? 'Join Group Purchase (Owner)' 
                : isGroupEnded
                  ? 'Group Ended - View Only'
                  : !canPurchase 
                    ? 'View Only - Not Invited' 
                    : 'Join Group Purchase'}
            </button>
            {!canPurchase && !isGroupOwner && !isGroupEnded && (
              <div style={{
                marginTop: 12,
                padding: '12px',
                borderRadius: 8,
                background: 'rgba(255, 184, 77, 0.1)',
                border: '1px solid rgba(255, 184, 77, 0.3)',
                color: '#ffb84d',
                fontSize: 12,
                textAlign: 'center',
              }}>
                ⚠️ You are not invited to this group. You can view the group details but cannot make a purchase.
              </div>
            )}
            <button style={{ ...secondaryButton, marginTop: 12, width: '100%' }} onClick={() => setShowPharmacySidebar(true)}>
              View Adhering Pharmacies
            </button>
          </section>
        )}

        <aside style={card}>
          {role === 'ADMIN' && isGroupOwner && (
            <div style={{ marginBottom: 20 }}>
              <h3>Admin Overview</h3>
              <p style={{ fontSize: 12, opacity: 0.7 }}>Execution rules, aggregation and audit</p>
            </div>
          )}
          <h3>Aggregated Volume</h3>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, opacity: 0.7 }}>
              <span>Time remaining</span>
              <span>
                {remainingMs <= 0 ? 'Ended' : `${remainingHours}h ${remainingMinutes}m`}
              </span>
            </div>
            <div style={{ height: 6, background: '#1a1f33', borderRadius: 8, marginTop: 6 }}>
              <div
                style={{
                  width: `${Math.min(100, Math.max(0, timeProgress))}%`, height: '100%', borderRadius: 8,
                  background: remainingMs <= 0 
                    ? 'linear-gradient(90deg,#ff6b6b,#ff6b6b)' 
                    : 'linear-gradient(90deg,#ffb84d,#ff6b6b)',
                }}
              />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <AggregateHeader style={{ fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
              <strong>Product</strong>
              <strong>Qty</strong>
              <strong>Value</strong>
              <strong>Pharmacies</strong>
              <strong>Tier score</strong>
            </AggregateHeader>
            {aggregatedByProduct.map((p) => (
              <AggregateRow
                key={p.id}
                style={{
                  fontSize: 13,
                  padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <span>{p.name}</span>
                <span>{p.qty.toLocaleString()}</span>
                <span>€{p.value.toFixed(2)}</span>
                <span
                  style={{
                    fontSize: 14, 
                    padding: '5px 32px',
                    borderRadius: 999,
                    background: 'rgba(80, 90, 110, 0.5)',
                    color: p.pharmaciesCount >= 4 
                      ? '#00f7d2' 
                      : p.pharmaciesCount >= 2 
                        ? '#5ce9ff' 
                        : '#f5a623',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 500,
                  }}
                >
                  {p.pharmaciesCount}
                </span>
                <span style={{ fontSize: 12, opacity: 0.8, textAlign: 'center' }}>{p.weightedScore.toFixed(1)}</span>
              </AggregateRow>
            ))}
            <p>{current.toLocaleString()} / {target.toLocaleString()} units</p>
          </div>
          <div style={{ height: 8, background: '#1a1f33', borderRadius: 8 }}>
            <div
              style={{
                width: `${progress}%`, height: '100%', borderRadius: 8,
                background: 'linear-gradient(90deg,#00f7d2,#5ce9ff)',
              }}
            />
          </div>
          {isGroupOwner && (
            <>
              <div style={{ marginTop: 20 }}>
                <button
                  disabled={!isExecutable || executed}
                  onClick={() => {
                    setExecuted(true);
                    setAuditLog([...auditLog, { ts: new Date().toISOString(), event: 'Master Order executed' }]);
                  }}
                  style={{ ...primaryButton, ...(executed || !isExecutable ? disabledButton : {}) }}
                >
                  {executed ? 'Executed' : 'Execute Master Order'}
                </button>
                <p style={{ fontSize: 11, opacity: 0.6, marginTop: 8 }}>Auto-exec when MOQ reached and at least 70 percent PAID</p>
              </div>
              <div style={{ marginTop: 24 }}>
                <h4>Audit Timeline</h4>
                {auditLog.map((a, i) => (
                  <div key={i} style={{ fontSize: 12, opacity: 0.75, marginBottom: 6 }}>
                    <strong>{a.ts}</strong> · {a.event}
                  </div>
                ))}
              </div>
            </>
          )}
        </aside>
      </MainLayout>

      {showAddProductModal && (
          <div
            style={{
              position: 'fixed', top: 60, right: 0, width: 380, height: 'calc(100vh - 60px)',
              background: '#0b0e14', borderLeft: '1px solid rgba(255,255,255,0.08)', padding: 20, zIndex: 1500, overflow: 'hidden',
              display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
            }}
          >
            <div style={{ flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Add product to Group</h3>
                <button onClick={() => {
                  setShowAddProductModal(false);
                  setProductSearchTerm('');
                  setSelectedProductsInModal([]);
                }} style={secondaryButton}>Close</button>
              </div>
              <p style={{ fontSize: 13, opacity: 0.7, margin: '0 0 16px 0' }}>Select a product from your store catalog</p>
              <input
                type="text"
                placeholder="Search products..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  marginBottom: 16,
                  background: '#0b0e14',
                  border: '1px solid #2a2f45',
                  color: '#fff',
                  borderRadius: 8,
                  padding: 10,
                  fontSize: 14,
                }}
              />
            </div>
            <div
              ref={addProductScrollRef}
              style={{
                overflowY: 'auto', flex: '1 1 0', minHeight: 0,
                paddingRight: 4,
                marginBottom: 12,
              }}
            >
              {!storeProductsLoaded ? (
                <p style={{ fontSize: 13, opacity: 0.7 }}>Loading products...</p>
              ) : (() => {
                const availableProducts = storeProducts.filter(
                  (sp) => !adminProducts.find((ap) => ap.id === sp.id)
                );
                const filteredProducts = availableProducts.filter((p) =>
                  p.name.toLowerCase().includes(productSearchTerm.toLowerCase())
                );
                return filteredProducts.length === 0 ? (
                  <p style={{ fontSize: 13, opacity: 0.7, padding: '16px 0' }}>
                    {availableProducts.length === 0
                      ? (storeProducts.length === 0
                          ? 'No products in your store. Add products to your store first.'
                          : 'All products are already added to this action.')
                      : 'No products match your search.'}
                  </p>
                ) : (
                  <>
                    {filteredProducts.map((p) => {
                      const isSelected = selectedProductsInModal.includes(p.id);
                      return (
                        <label
                          key={p.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.06)',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedProductsInModal([...selectedProductsInModal, p.id]);
                              } else {
                                setSelectedProductsInModal(selectedProductsInModal.filter((id) => id !== p.id));
                              }
                            }}
                            style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>€{Number(p.price).toFixed(2)}</div>
                          </div>
                        </label>
                      );
                    })}
                    <div ref={addProductSentinelRef} style={{ minHeight: 20, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 12 }}>
                      {isLoadingMore && <span style={{ fontSize: 12, opacity: 0.7 }}>Loading more...</span>}
                      {!isLoadingMore && storeProducts.length < totalProductsCount && storeProducts.length > 0 && (
                        <span style={{ fontSize: 11, opacity: 0.5 }}>Scroll for more</span>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            <div
              style={{
                paddingTop: 12,
                paddingBottom: 8,
                borderTop: '1px solid rgba(255,255,255,0.08)',
                flexShrink: 0,
                background: '#0b0e14',
              }}
            >
              <button
                onClick={() => {
                  if (selectedProductsInModal.length === 0) {
                    toast.error('Please select at least one product');
                    return;
                  }
                  const productsToAdd = storeProducts
                    .filter((p) => selectedProductsInModal.includes(p.id))
                    .map((p) => ({
                      id: p.id,
                      name: p.name,
                      price: Number(p.price),
                      maxQty: 1000,
                      category_id: p.category_id || null,
                      vat: p.vat || 0,
                      image: p.image || null,
                    }));
                  setAdminProducts([...adminProducts, ...productsToAdd]);
                  const productNames = productsToAdd.map((p) => p.name).join(', ');
                  setAuditLog([
                    ...auditLog,
                    { ts: new Date().toISOString(), event: `Products added to action: ${productNames}` },
                  ]);
                  toast.success(`${productsToAdd.length} product(s) added successfully`);
                  setShowAddProductModal(false);
                  setProductSearchTerm('');
                  setSelectedProductsInModal([]);
                }}
                disabled={selectedProductsInModal.length === 0}
                style={{
                  ...primaryButton,
                  width: '100%',
                  ...(selectedProductsInModal.length === 0 ? disabledButton : {}),
                }}
              >
                Add Selected ({selectedProductsInModal.length})
              </button>
            </div>
          </div>
        )}

        {selectedProduct && (
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
            onClick={() => setSelectedProduct(null)}
          >
            <div style={{ ...card, width: 420 }} onClick={(e) => e.stopPropagation()}>
              {(() => {
                const prod = storeProducts.find((p) => p.id === selectedProduct);
                if (!prod) return null;
                return (
                  <>
                    <h3>{prod.name}</h3>
                    <p style={{ fontSize: 13, opacity: 0.7 }}>{prod.description}</p>
                    <p><strong>Unit price:</strong> €{Number(prod.price).toFixed(2)}</p>
                    <button style={{ ...secondaryButton, marginTop: 12 }} onClick={() => setSelectedProduct(null)}>Close</button>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {showInviteSidebar && (
          <div
            style={{
              position: 'fixed', top: 60, right: 0, 
              width: showEditGroupSidebar ? 480 : 380, 
              height: 'calc(100vh - 60px)',
              background: '#0b0e14', borderLeft: '1px solid rgba(255,255,255,0.08)', padding: 20, 
              zIndex: showEditGroupSidebar ? 2100 : 1500,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3>{showEditGroupSidebar ? 'Select Pharmacies' : 'Invite Pharmacies'}</h3>
              <button onClick={() => {
                if (showEditGroupSidebar) {
                  // Sync selected pharmacies back to edit group data
                  setEditGroupData((prev) => ({ ...prev, invited_users: selectedPharmacies }));
                }
                setShowInviteSidebar(false);
                setEditingGroupId(null);
                setInviteGroupName('');
                if (!showEditGroupSidebar) {
                  setSelectedPharmacies([]);
                }
              }} style={secondaryButton}>{showEditGroupSidebar ? 'Done' : 'Close'}</button>
            </div>
            {!showEditGroupSidebar && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, opacity: 0.7, display: 'block', marginBottom: 6 }}>Group Name</label>
                <input
                  placeholder="Enter group name"
                  value={inviteGroupName}
                  onChange={(e) => setInviteGroupName(e.target.value)}
                  style={{ width: '100%', background: '#0b0e14', border: '1px solid #2a2f45', color: '#fff', borderRadius: 8, padding: 8 }}
                />
              </div>
            )}
            <input
              placeholder="Search by pharmacy name"
              value={pharmacySearch}
              onChange={(e) => setPharmacySearch(e.target.value)}
              style={{ width: '100%', marginBottom: 8, background: '#0b0e14', border: '1px solid #2a2f45', color: '#fff', borderRadius: 8, padding: 8 }}
            />
            <select
              value={pharmacyCityFilter}
              onChange={(e) => setPharmacyCityFilter(e.target.value)}
              style={{ width: '100%', marginBottom: 12, background: '#0b0e14', border: '1px solid #2a2f45', color: '#fff', borderRadius: 8, padding: 8 }}
            >
              <option value="ALL">All locations</option>
              {[...new Set(allPharmacies.map((p) => p.city).filter(Boolean))].map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
            {savedInviteGroups.length > 0 && !showEditGroupSidebar && (
              <div style={{ marginBottom: 12 }}>
                <h4 style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>
                  {editingGroupId ? 'Editing group' : 'Saved groups – select to edit'}
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {savedInviteGroups.map((g) => {
                    const isEditing = editingGroupId === g.id;
                    const isActive = isEditing || ((g.pharmacyIds?.length || 0) > 0 && selectedPharmacies.length === (g.pharmacyIds?.length || 0) && (g.pharmacyIds || []).every((id) => selectedPharmacies.includes(id)));
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => {
                          setEditingGroupId(g.id);
                          setInviteGroupName(g.name);
                          setSelectedPharmacies(g.pharmacyIds || []);
                        }}
                        style={{
                          ...secondaryButton,
                          padding: '8px 14px',
                          borderRadius: 10,
                          fontSize: 13,
                          flexShrink: 0,
                          color: isActive ? '#0b0e14' : '#eaeaf0',
                          borderColor: isActive ? 'rgba(0,247,210,0.6)' : undefined,
                          background: isActive ? 'rgba(0,247,210,0.25)' : '#0b0e14',
                        }}
                      >
                        {g.name} {g.pharmacyIds?.length ? `(${g.pharmacyIds.length})` : ''}
                        {isEditing && ' ✏️'}
                      </button>
                    );
                  })}
                </div>
                {editingGroupId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingGroupId(null);
                      setInviteGroupName('');
                      setSelectedPharmacies([]);
                    }}
                    style={{
                      ...secondaryButton,
                      marginTop: 8,
                      padding: '6px 12px',
                      fontSize: 12,
                    }}
                  >
                    Cancel Edit / Create New
                  </button>
                )}
              </div>
            )}
            <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0, maxHeight: 'calc(100vh - 400px)', paddingRight: 4, marginBottom: 12 }}>
              {!pharmaciesLoaded ? (
                <p style={{ fontSize: 13, opacity: 0.7 }}>Loading pharmacies...</p>
              ) : (() => {
                const filtered = allPharmacies.filter((p) => {
                  const matchName = p.name.toLowerCase().includes(pharmacySearch.toLowerCase());
                  const matchCity = pharmacyCityFilter === 'ALL' || p.city === pharmacyCityFilter;
                  return matchName && matchCity;
                });
                return filtered.length === 0 ? (
                  <p style={{ fontSize: 13, opacity: 0.7 }}>No pharmacies found.</p>
                ) : (
                  filtered.map((p) => (
                    <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedPharmacies.includes(p.id)}
                        onChange={() => {
                          setSelectedPharmacies((prev) =>
                            prev.includes(p.id) ? prev.filter((id) => id !== p.id) : [...prev, p.id]
                          );
                        }}
                      />
                      <span style={{ fontSize: 13 }}>{p.name}</span>
                      {p.city && <span style={{ fontSize: 11, opacity: 0.6 }}>({p.city})</span>}
                    </label>
                  ))
                );
              })()}
            </div>
       
            {!showEditGroupSidebar && (
              <div style={{ marginTop: 'auto', paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
                <button
                  onClick={async () => {
                    if (!inviteGroupName.trim()) {
                      toast.error('Please enter a group name');
                      return;
                    }
                    if (selectedPharmacies.length === 0) {
                      toast.error('Please select at least one pharmacy');
                      return;
                    }
                    setIsSavingInvitedGroup(true);
                    try {
                      if (editingGroupId) {
                        // Use update-invited-group API for editing
                        const updatePayload = {
                          group_name: inviteGroupName.trim(),
                          invited_users: selectedPharmacies,
                          record_id: editingGroupId,
                        };
                        
                        console.log("🚀 ~ Updating group with payload:", updatePayload);
                        const response = await updateInvitedGroup(updatePayload);
                        console.log("🚀 ~ Update response:", response);
                        
                        if (response.data?.success || response.status === 200) {
                          toast.success('Group updated successfully');
                          // Reload saved groups to get latest data from server
                          await loadSavedGroups();
                          setInviteGroupName('');
                          setEditingGroupId(null);
                          setSelectedPharmacies([]);
                          // Close sidebar after successful save
                          setShowInviteSidebar(false);
                        } else {
                          toast.error('Failed to update group');
                        }
                      } else {
                        // Use save-invited-group API for creating new group
                        const createPayload = {
                          group_name: inviteGroupName.trim(),
                          invited_users: selectedPharmacies,
                        };
                        
                        console.log("🚀 ~ Creating group with payload:", createPayload);
                        const response = await saveInvitedGroup(createPayload);
                        console.log("🚀 ~ Create response:", response);
                        
                        if (response.data?.success || response.status === 200) {
                          toast.success('Group saved successfully');
                          // Reload saved groups to get latest data from server
                          await loadSavedGroups();
                          setInviteGroupName('');
                          setEditingGroupId(null);
                          setSelectedPharmacies([]);
                          // Close sidebar after successful save
                          setShowInviteSidebar(false);
                        } else {
                          toast.error('Failed to save group');
                        }
                      }
                    } catch (error) {
                      console.error('Error saving/updating group:', error);
                      toast.error(error.response?.data?.message || (editingGroupId ? 'Failed to update group' : 'Failed to save group'));
                    } finally {
                      setIsSavingInvitedGroup(false);
                    }
                  }}
                  disabled={isSavingInvitedGroup || !inviteGroupName.trim() || selectedPharmacies.length === 0}
                  style={{
                    ...primaryButton,
                    width: '100%',
                    ...(isSavingInvitedGroup || !inviteGroupName.trim() || selectedPharmacies.length === 0 ? disabledButton : {}),
                  }}
                >
                  {isSavingInvitedGroup
                    ? (editingGroupId ? 'Updating...' : 'Saving...')
                    : (editingGroupId ? 'Update Group' : 'Save Group')}
                </button>
              </div>
            )}
          </div>
        )}

        {showPharmacySidebar && (
          <div
            style={{
              position: 'fixed', top: 60, right: 0, width: 360, height: 'calc(100vh - 60px)',
              background: '#0b0e14', borderLeft: '1px solid rgba(255,255,255,0.08)', padding: 20, zIndex: 1000,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexShrink: 0 }}>
              <h3>Adhering Pharmacies</h3>
              <button onClick={() => setShowPharmacySidebar(false)} style={secondaryButton}>Close</button>
            </div>
            <div style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0, paddingRight: 4 }}>
              {(() => {
                const adheringPharmacies = getAdheringPharmacies();
                console.log("🚀 ~ sidebar ~ adheringPharmacies:", adheringPharmacies);
                console.log("🚀 ~ sidebar ~ groupDetail:", groupDetail);
                
                if (adheringPharmacies.length === 0) {
                  return (
                    <div style={{ padding: '16px 0' }}>
                      <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 12 }}>
                        No user data available. Please check the API response.
                      </p>
                      {groupDetail && (
                        <div style={{ fontSize: 11, opacity: 0.5, fontFamily: 'monospace', background: 'rgba(0,0,0,0.3)', padding: 8, borderRadius: 4, overflow: 'auto', maxHeight: 200 }}>
                          <div><strong>groupDetail keys:</strong> {Object.keys(groupDetail).join(', ')}</div>
                          <div><strong>Has user array:</strong> {Array.isArray(groupDetail.user) ? `Yes (${groupDetail.user.length})` : 'No'}</div>
                          <div><strong>Has locations array:</strong> {Array.isArray(groupDetail.locations) ? `Yes (${groupDetail.locations.length})` : 'No'}</div>
                          {groupDetail.user && <div><strong>First user:</strong> {JSON.stringify(groupDetail.user[0], null, 2)}</div>}
                        </div>
                      )}
                    </div>
                  );
                }
                
                const orderData = groupDetail?.order_data;
                const orderDetails = Array.isArray(orderData) && orderData.length > 0 
                  ? orderData[0]?.order_details || [] 
                  : [];
                
                return adheringPharmacies.map((pharmacy) => {
                  const matchingOrder = orderDetails.find(
                    (order) => order.buyer_user_id === pharmacy.id
                  );
                  const paymentStatus = matchingOrder?.Payment_status || null;
                  
                  const getStatusBadge = (status) => {
                    if (!status) return null;
                    const statusLower = status.toLowerCase();
                    let bgColor, textColor;
                    
                    if (statusLower === 'paid') {
                      bgColor = 'rgba(45, 212, 191, 0.25)';
                      textColor = '#2dd4bf';
                    } else if (statusLower === 'confirmed') {
                      bgColor = 'rgba(45, 212, 191, 0.25)';
                      textColor = '#2dd4bf';
                    } else if (statusLower === 'pending' || statusLower === 'waiting') {
                      bgColor = 'rgba(251, 146, 60, 0.25)';
                      textColor = '#fb923c';
                    } else if (statusLower === 'failed' || statusLower === 'cancelled') {
                      bgColor = 'rgba(239, 68, 68, 0.25)';
                      textColor = '#ef4444';
                    } else {
                      bgColor = 'rgba(148, 163, 184, 0.25)';
                      textColor = '#94a3b8';
                    }
                    
                    return (
                      <span
                        style={{
                          background: bgColor,
                          color: textColor,
                          padding: '4px 14px',
                          borderRadius: 16,
                          fontSize: 11,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {status.toUpperCase()}
                      </span>
                    );
                  };

                  return (
                    <div key={pharmacy.id} style={{ padding: '16px 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 6, color: '#fff' }}>
                        {pharmacy.name}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                          Trial
                        </span>
                        {paymentStatus && getStatusBadge(paymentStatus)}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        <PaymentSidebar
          open={showPaymentSidebar}
          onOpenChange={setShowPaymentSidebar}
          amountToPay={totalAmountToPay}
          productSymbol={groupDetail?.product_detail?.[0]?.symbol || null}
          groupId={groupId || groupDetail?._id || groupDetail?.id}
          orderDetail={pharmacyProducts.filter((p) => selectedProductsToBuy[p.key] && productQuantities[p.key]).map((p) => {
            const product = adminProducts.find((ap) => ap.id === p.key);
            return {
              product_id: p.key,
              quantity: productQuantities[p.key] || 0,
              price: product?.price || 0,
              vat: product?.vat || 0, // Include VAT in orderDetail
            };
          })}
          products={adminProducts.map((p) => ({ id: p.id, name: p.name, price: p.price, category_id: p.category_id || null, image: p.image || null }))}
          shippingCost={shippingCost}
          displayCurrency={groupDetail?.product_detail?.[0]?.symbol || 'USD'}
          categoryId={groupDetail?.product_detail?.[0]?.category_id || null}
          onPurchaseSuccess={onGroupDetailRefetch}
        />

        {/* Edit Group Sidebar */}
        {showEditGroupSidebar && (
          <div
            style={{
              position: 'fixed',
              top: 60,
              right: 0,
              width: 480,
              height: 'calc(100vh - 60px)',
              background: '#0b0e14',
              borderLeft: '1px solid rgba(0, 247, 210, 0.2)',
              padding: 24,
              zIndex: 2000,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.5)',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
              <h3 style={{ margin: 0, ...gradientTitle }}>Edit Group</h3>
              <button
                onClick={() => setShowEditGroupSidebar(false)}
                style={{ ...secondaryButton, padding: '8px 16px' }}
              >
                Close
              </button>
            </div>

            {/* Scrollable Content */}
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 8 }}>
              {/* Group Name */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, opacity: 0.7, display: 'block', marginBottom: 6 }}>Group Name</label>
                <input
                  value={editGroupData.group_name}
                  onChange={(e) => setEditGroupData((prev) => ({ ...prev, group_name: e.target.value }))}
                  style={{
                    width: '100%',
                    background: '#111522',
                    border: '1px solid #2a2f45',
                    color: '#fff',
                    borderRadius: 10,
                    padding: 12,
                  }}
                />
              </div>

              {/* Date Range */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div>
                  <label style={{ fontSize: 12, opacity: 0.7, display: 'block', marginBottom: 6 }}>Start Date</label>
                  <DateInput
                    type="datetime-local"
                    value={editGroupData.start_time}
                    onChange={(e) => setEditGroupData((prev) => ({ ...prev, start_time: e.target.value }))}
                    style={{
                      width: '100%',
                      background: '#111522',
                      border: '1px solid #2a2f45',
                      color: '#fff',
                      borderRadius: 10,
                      padding: 10,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, opacity: 0.7, display: 'block', marginBottom: 6 }}>End Date</label>
                  <DateInput
                    type="datetime-local"
                    value={editGroupData.end_time}
                    onChange={(e) => setEditGroupData((prev) => ({ ...prev, end_time: e.target.value }))}
                    style={{
                      width: '100%',
                      background: '#111522',
                      border: '1px solid #2a2f45',
                      color: '#fff',
                      borderRadius: 10,
                      padding: 10,
                    }}
                  />
                </div>
              </div>

              {/* Status */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontSize: 12, opacity: 0.7, display: 'block', marginBottom: 6 }}>Status</label>
                <select
                  value={editGroupData.status}
                  onChange={(e) => setEditGroupData((prev) => ({ ...prev, status: e.target.value }))}
                  style={{
                    width: '100%',
                    background: '#111522',
                    border: '1px solid #2a2f45',
                    color: '#fff',
                    borderRadius: 10,
                    padding: 12,
                    cursor: 'pointer',
                  }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Invited Pharmacies Section */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>Invited Pharmacies ({editGroupData.invited_users.length})</label>
                </div>
                
                {/* Selected pharmacies as chips */}
                {editGroupData.invited_users.length > 0 && (
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 8, 
                    marginBottom: 12,
                    padding: 12,
                    background: '#111522',
                    borderRadius: 10,
                    border: '1px solid rgba(0,247,210,0.2)',
                  }}>
                    {editGroupData.invited_users.map((userId) => {
                      const pharmacy = allPharmacies.find((p) => p.id === userId);
                      return (
                        <div
                          key={userId}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 10px',
                            background: 'rgba(0, 247, 210, 0.1)',
                            border: '1px solid rgba(0, 247, 210, 0.3)',
                            borderRadius: 20,
                            fontSize: 12,
                            color: '#00f7d2',
                          }}
                        >
                          <span>{pharmacy?.name || userId.slice(0, 8) + '...'}</span>
                          <button
                            onClick={() => handleRemovePharmacyFromEdit(userId)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#00f7d2',
                              cursor: 'pointer',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 16,
                              lineHeight: 1,
                            }}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Button to open pharmacy sidebar */}
                <button
                  onClick={() => {
                    setSelectedPharmacies(editGroupData.invited_users);
                    setShowInviteSidebar(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'rgba(0, 247, 210, 0.1)',
                    border: '1px solid rgba(0, 247, 210, 0.3)',
                    borderRadius: 10,
                    color: '#00f7d2',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 16 }}>+</span>
                  <span>Add / Edit Pharmacies</span>
                </button>
              </div>

              {/* Products Section */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <label style={{ fontSize: 14, fontWeight: 600 }}>Products ({editGroupData.product_details.length})</label>
                  <button
                    onClick={() => setShowEditProductModal(true)}
                    style={{
                      ...secondaryButton,
                      padding: '8px 14px',
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span style={{ fontSize: 16 }}>+</span> Add New Product
                  </button>
                </div>

                {/* Existing products with quantity */}
                <div style={{ maxHeight: 250, overflowY: 'auto' }}>
                  {editGroupData.product_details.length === 0 ? (
                    <div style={{ padding: 12, opacity: 0.6, fontSize: 12, textAlign: 'center', background: '#111522', borderRadius: 10 }}>
                      No products added yet
                    </div>
                  ) : (
                    editGroupData.product_details.map((pd) => (
                      <div
                        key={pd.product_id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px',
                          background: '#111522',
                          borderRadius: 8,
                          marginBottom: 8,
                          border: '1px solid rgba(0,247,210,0.2)',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, marginBottom: 4 }}>{pd.name || pd.product_id}</div>
                          <div style={{ fontSize: 11, opacity: 0.6 }}>Price: ${pd.price}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div>
                            <label style={{ fontSize: 10, opacity: 0.6, display: 'block' }}>Qty</label>
                            <input
                              type="number"
                              value={pd.quantity}
                              onChange={(e) => handleUpdateProductQuantityInEdit(pd.product_id, e.target.value)}
                              style={{
                                width: 70,
                                background: '#0b0e14',
                                border: '1px solid #2a2f45',
                                color: '#fff',
                                borderRadius: 6,
                                padding: 6,
                                textAlign: 'center',
                              }}
                              min={1}
                            />
                          </div>
                          <button
                            onClick={() => handleRemoveProductFromEdit(pd.product_id)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.15)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              color: '#ef4444',
                              borderRadius: 6,
                              padding: '6px 10px',
                              fontSize: 12,
                              cursor: 'pointer',
                              marginTop: 14,
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer with Save button */}
            <div style={{ flexShrink: 0, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <button
                onClick={handleUpdateGroup}
                disabled={isUpdatingGroup}
                style={{
                  ...primaryButton,
                  opacity: isUpdatingGroup ? 0.6 : 1,
                  cursor: isUpdatingGroup ? 'not-allowed' : 'pointer',
                }}
              >
                {isUpdatingGroup ? 'Updating...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* Edit Group Sidebar Overlay */}
        {showEditGroupSidebar && (
          <div
            onClick={() => setShowEditGroupSidebar(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1999,
            }}
          />
        )}

        {/* Add Product Modal for Edit Group */}
        {showEditProductModal && (
          <div
            style={{
              position: 'fixed',
              top: 60,
              right: 0,
              width: 380,
              height: 'calc(100vh - 60px)',
              background: '#0b0e14',
              borderLeft: '1px solid rgba(255,255,255,0.08)',
              padding: 20,
              zIndex: 2100,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Add Products to Group</h3>
                <button
                  onClick={() => {
                    setShowEditProductModal(false);
                    setSelectedProductsInEditModal([]);
                  }}
                  style={secondaryButton}
                >
                  Close
                </button>
              </div>
              <p style={{ fontSize: 13, opacity: 0.7, margin: '0 0 16px 0' }}>
                Select products to add to the group
              </p>
              <input
                type="text"
                placeholder="Search products..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  marginBottom: 16,
                  background: '#0b0e14',
                  border: '1px solid #2a2f45',
                  color: '#fff',
                  borderRadius: 8,
                  padding: 10,
                  fontSize: 14,
                }}
              />
            </div>
            <div
              ref={addProductScrollRef}
              style={{
                overflowY: 'auto',
                flex: '1 1 0',
                minHeight: 0,
                paddingRight: 4,
                marginBottom: 12,
              }}
            >
              {!storeProductsLoaded ? (
                <p style={{ fontSize: 13, opacity: 0.7 }}>Loading products...</p>
              ) : (() => {
                const alreadyAddedIds = editGroupData.product_details.map((pd) => pd.product_id);
                const availableProducts = storeProducts.filter(
                  (sp) => !alreadyAddedIds.includes(sp._id) && !alreadyAddedIds.includes(sp.id)
                );
                const filteredProducts = availableProducts.filter((p) =>
                  (p.title || p.name || '').toLowerCase().includes(productSearchTerm.toLowerCase())
                );
                return filteredProducts.length === 0 ? (
                  <p style={{ fontSize: 13, opacity: 0.7, padding: '16px 0' }}>
                    {availableProducts.length === 0
                      ? storeProducts.length === 0
                        ? 'No products in your store. Add products to your store first.'
                        : 'All available products are already added to this group.'
                      : 'No products match your search.'}
                  </p>
                ) : (
                  <>
                    {filteredProducts.map((p) => {
                      const productId = p._id || p.id;
                      const isSelected = selectedProductsInEditModal.includes(productId);
                      return (
                        <label
                          key={productId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '10px 0',
                            borderBottom: '1px solid rgba(255,255,255,0.06)',
                            cursor: 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedProductsInEditModal([...selectedProductsInEditModal, productId]);
                              } else {
                                setSelectedProductsInEditModal(
                                  selectedProductsInEditModal.filter((id) => id !== productId)
                                );
                              }
                            }}
                            style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 13,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {p.title || p.name}
                            </div>
                            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 2 }}>
                              ${Number(p.product_sizes_quantity?.[0]?.price || p.euro_price?.[0] || p.price || 0).toFixed(2)}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                    <div
                      ref={addProductSentinelRef}
                      style={{
                        minHeight: 20,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: 12,
                      }}
                    >
                      {isLoadingMore && <span style={{ fontSize: 12, opacity: 0.7 }}>Loading more...</span>}
                      {!isLoadingMore && storeProducts.length < totalProductsCount && storeProducts.length > 0 && (
                        <span style={{ fontSize: 11, opacity: 0.5 }}>Scroll for more</span>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
            <div
              style={{
                paddingTop: 12,
                paddingBottom: 8,
                borderTop: '1px solid rgba(255,255,255,0.08)',
                flexShrink: 0,
                background: '#0b0e14',
              }}
            >
              <button
                onClick={() => {
                  if (selectedProductsInEditModal.length === 0) {
                    toast.error('Please select at least one product');
                    return;
                  }
                  const productsToAdd = storeProducts
                    .filter((p) => selectedProductsInEditModal.includes(p._id || p.id))
                    .map((p) => ({
                      product_id: p._id || p.id,
                      name: p.title || p.name,
                      price: Number(p.product_sizes_quantity?.[0]?.price || p.euro_price?.[0] || p.price || 0),
                      quantity: 1000,
                    }));
                  setEditGroupData((prev) => ({
                    ...prev,
                    product_details: [...prev.product_details, ...productsToAdd],
                  }));
                  toast.success(`${productsToAdd.length} product(s) added successfully`);
                  setShowEditProductModal(false);
                  setProductSearchTerm('');
                  setSelectedProductsInEditModal([]);
                }}
                disabled={selectedProductsInEditModal.length === 0}
                style={{
                  ...primaryButton,
                  width: '100%',
                  ...(selectedProductsInEditModal.length === 0 ? disabledButton : {}),
                }}
              >
                Add Selected ({selectedProductsInEditModal.length})
              </button>
            </div>
          </div>
        )}

        {/* Edit Product Modal Overlay */}
        {showEditProductModal && (
          <div
            onClick={() => {
              setShowEditProductModal(false);
              setSelectedProductsInEditModal([]);
            }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              zIndex: 2050,
            }}
          />
        )}
    </div>
  );
}
